import React, { lazy, Suspense, useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Home from './Home';
import QuizSelection from './QuizSelection';
import Quiz from './Quiz';
import QuizResults from './QuizResults';
const QuizGenerator = lazy(() => import('./QuizGenerator'));
const GeneratedQuizScreen = lazy(() => import('./GeneratedQuizScreen'));
import MaterialEstudio from './MaterialEstudio';
import Bibliografia from './Bibliografia';
const IAVortex = lazy(() => import('./IAVortex'));
import VozVortex from './VozVortex';
import Comunidad from './Comunidad';
import Profile from './Profile';
import SimuladorExamen from './SimuladorExamen';
import TestResults from './TestResults';
import PlanEstudioMenu from './PlanEstudioMenu';
import PlanEstudioStudy from './PlanEstudioStudy';
import NotificationCenter from './NotificationCenter';
const AdminPanel = lazy(() => import('./AdminPanel'));
const AdminQuizzManagement = lazy(() => import('./AdminQuizzManagement'));
const AdminContentExplorer = lazy(() => import('./AdminContentExplorer'));
const AdminVaultCleaner = lazy(() => import('./AdminVaultCleaner'));
const AdminMaintenance = lazy(() => import('./AdminMaintenance'));
const AdminStudyMaterial = lazy(() => import('./AdminStudyMaterial'));
import WildcardManagement from './WildcardManagement';
import AudioLibrary from './AudioLibrary';
import Tutorials from './Tutorials';
import StudyProgress from './StudyProgress';
import LegalDocs from './LegalDocs';
import ChatList from './ChatList';
import ChatScreen from './ChatScreen';
import Settings from './Settings';
import Flashcards from './Flashcards';
import MapaCalorDebilidades from './MapaCalorDebilidades';
import ModoGuardia from './ModoGuardia';
import VortexVoices from './VortexVoices';
import DocViewer from './DocViewer';
import Vault from './Vault';
import VortexDocs from './VortexDocs';
const AdminContentGnSedena = lazy(() => import('./AdminContentGnSedena'));
const AdminCursosManagement = lazy(() => import('./AdminCursosManagement'));
const AdminAssignVault = lazy(() => import('./AdminAssignVault'));
const AdminLectorTemas = lazy(() => import('./admin/AdminLectorTemas'));
const AdminClonarVoz = lazy(() => import('./admin/AdminClonarVoz'));
const AdminLayout = lazy(() => import('./AdminLayout'));

// Importar PWA & Responsive additions
import BottomNavigationBar from './BottomNavigationBar';
import OfflineIndicator from './OfflineIndicator';
import InstallPrompt from './InstallPrompt';
import IOSInstallGuide from './IOSInstallGuide';
import usePWAInstall from '../hooks/usePWAInstall';

import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAdmin } from '../hooks/useAdmin';
import { getDatabase, ref, set, update } from 'firebase/database';
import config from '../config/environment';
import { useShadowMode } from '../contexts/ShadowModeContext';
import '../styles/global.css';

const Dashboard = () => {
  const { user, deviceId } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin(user);
  const { isInstallable, isStandalone, isIOS, triggerInstall } = usePWAInstall();
  const { isShadowActive, shadowUid, shadowUserData } = useShadowMode();
  const location = useLocation();
  
  const isAdminRoute = location.pathname.includes('/admin') || location.pathname.includes('/simulador') || location.pathname.includes('/test-results') || location.pathname.includes('/vault');
  // Solo rutas estrictamente de admin (no simulador, vault, etc.)
  const isStrictAdminRoute = location.pathname.includes('/admin');
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showInstallPrompt, setShowInstallPrompt] = useState(true);
  const [showIOSGuide, setShowIOSGuide] = useState(true);
  const [showBrowserGuide, setShowBrowserGuide] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user && deviceId && !isShadowActive) {
      const rdb = getDatabase();
      const appVersion = config.APP_VERSION || '1.0.0';
      const sanitizedVersion = appVersion.replace(/\./g, ',');
      const uid = user.uid;
      const now = Date.now();

      // 1. Escribir sesión web
      const versionRef = ref(rdb, `userVersions/${uid}/web_session`);
      set(versionRef, {
        deviceId: deviceId,
        lastAccess: now,
        platform: 'Web Browser',
        userAgent: navigator.userAgent
      }).catch(err => console.error("Error syncing device info:", err));

      // 2. Escribir datos de versión del usuario (merge con update)
      update(ref(rdb, `userVersions/${uid}`), {
        currentVersion: { versionName: appVersion },
        lastLogin: now,
        userEmail: user.email || '',
        userId: uid,
        platform: 'Web Browser',
      }).catch(err => console.error("Error writing user version:", err));

      // 3. Registrar en el índice de versiones (formato compatible con reglas RTDB)
      update(ref(rdb, `admin/versionIndex/${sanitizedVersion}`), {
        __name: appVersion,
        [uid]: true
      }).catch(err => {
        if (err?.code !== 'PERMISSION_DENIED' && !err?.message?.includes('PERMISSION_DENIED')) {
          console.warn("Notice updating version index:", err?.message || err);
        }
      });

      // 4. Actualizar lastLogin en users/{uid}
      update(ref(rdb, `users/${uid}`), { lastLogin: now }).catch(err => console.error("Error updating lastLogin:", err));
    }
  }, [user, deviceId]);

  const adminOnlyRoute = (element) => {
    if (adminLoading) return <div className="route-loading" role="status" aria-live="polite">Cargando módulo...</div>;
    return isAdmin ? element : <Navigate to="/dashboard" replace />;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontFamily: 'var(--font-primary)',
      display: 'flex',
      flexDirection: 'row',
    }}>
      <a href="#main-content" className="skip-link">Saltar al contenido principal</a>
      <OfflineIndicator />
      
      {/* Sidebar de usuario: se oculta en rutas de admin para que el sub-sidebar del AdminPanel sea la navegación exclusiva */}
      {!isStrictAdminRoute && <Sidebar />}
      
      <main id="main-content" style={{
        flex: 1,
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingTop: isStrictAdminRoute ? '0' : (isMobile ? '70px' : 'var(--spacing-xl)'),
        paddingBottom: isStrictAdminRoute ? '0' : (isMobile ? '80px' : 'var(--spacing-xl)'),
        paddingLeft: isStrictAdminRoute ? '0' : (isMobile ? '16px' : (isAdminRoute ? 'calc(210px + 10px)' : 'calc(210px + var(--spacing-xl))')),
        paddingRight: isStrictAdminRoute ? '0' : (isMobile ? '16px' : (isAdminRoute ? '10px' : 'var(--spacing-xl)')),
        marginLeft: '0px',
        minHeight: '100vh',
        boxSizing: 'border-box',
        minWidth: 0,
      }}>
        <div style={{
          maxWidth: '100%',
          padding: isAdminRoute ? '0' : '0 var(--container-padding)'
        }}>
          <Suspense fallback={<div className="route-loading" role="status">Cargando módulo...</div>}>
          <Routes>
            <Route index element={<Home />} />
            <Route path="quiz" element={<QuizSelection />} />
            <Route path="quiz/:quizId" element={<Quiz />} />
            <Route path="quiz-results" element={<QuizResults />} />
            <Route path="quiz-generator" element={adminOnlyRoute(<QuizGenerator />)} />
            <Route path="quiz-generator/:quizId" element={adminOnlyRoute(<GeneratedQuizScreen />)} />
            <Route path="material" element={<MaterialEstudio />} />
            <Route path="bibliografia" element={<Bibliografia />} />
            <Route path="ia-vortex" element={adminOnlyRoute(<IAVortex />)} />
            <Route path="voz-vortex" element={<VozVortex />} />
            <Route path="comunidad/*" element={<Comunidad />} />
            <Route path="perfil" element={<Profile />} />
            <Route path="notificaciones" element={<NotificationCenter />} />
            <Route path="simulador" element={<SimuladorExamen />} />
            <Route path="test-results" element={<TestResults />} />
            <Route path="plan-estudio" element={<PlanEstudioMenu />} />
            <Route path="plan-estudio/:planId" element={<PlanEstudioStudy />} />
            <Route path="admin" element={adminOnlyRoute(<AdminLayout />)}>
              <Route index element={<AdminPanel />} />
              <Route path="cursos" element={<AdminCursosManagement />} />
              <Route path="lector-temas" element={<AdminLectorTemas />} />
              <Route path="quizz-management" element={<AdminQuizzManagement />} />
              <Route path="content-explorer" element={<AdminContentExplorer />} />
              <Route path="vault-cleaner" element={<AdminVaultCleaner />} />
              <Route path="maintenance" element={<AdminMaintenance />} />
              <Route path="study-material" element={<AdminStudyMaterial />} />
              <Route path="gn-sedena-content" element={<AdminContentGnSedena />} />
              <Route path="wildcards" element={<WildcardManagement />} />
              <Route path="vortex-docs" element={adminOnlyRoute(<VortexDocs />)} />
              <Route path="assign-vault" element={<AdminAssignVault />} />
              <Route path="clonar-voz" element={<AdminClonarVoz />} />
            </Route>
            
            <Route path="audios" element={<AudioLibrary />} />
            <Route path="flashcards" element={<Flashcards />} />
            <Route path="mapa-debilidades" element={<MapaCalorDebilidades />} />
            <Route path="modo-guardia" element={<ModoGuardia />} />
            <Route path="tutorials" element={<Tutorials />} />
            <Route path="study-progress" element={<StudyProgress />} />
            <Route path="legal" element={<LegalDocs />} />
            <Route path="chat" element={<ChatList />} />
            <Route path="chat/:chatId" element={<ChatScreen />} />
            {/* <Route path="vortex-voices" element={<VortexVoices />} /> */}
            <Route path="doc-viewer" element={<DocViewer />} />
            <Route path="vault" element={<Vault />} />
            <Route path="configuracion" element={<Settings />} />
            {/* Aquí se agregarán las demás rutas internas */}
          </Routes>
          </Suspense>
        </div>
      </main>

      {/* Navegación inferior y control de prompts específicos para móvil y web */}
      {isMobile && <BottomNavigationBar />}
      
      {isInstallable && showInstallPrompt && !isStandalone && (
        <InstallPrompt triggerInstall={triggerInstall} onClose={() => setShowInstallPrompt(false)} />
      )}
      
      {isIOS && showIOSGuide && !isStandalone && (
        <IOSInstallGuide onClose={() => setShowIOSGuide(false)} />
      )}

      {/* Botón Flotante Permanente de Instalación (Visible si no está instalada en Standalone) */}
      {!isStandalone && (
        <button
          onClick={() => {
            if (isIOS) {
              setShowIOSGuide(true);
            } else if (isInstallable) {
              triggerInstall();
            } else {
              setShowBrowserGuide(true);
            }
          }}
          style={{
            position: 'fixed',
            bottom: isMobile ? '80px' : '24px',
            right: '16px',
            zIndex: 1050,
            background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)',
            border: '1px solid var(--gold-light)',
            borderRadius: '30px',
            padding: '12px 18px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: '0.78rem',
            fontWeight: 'bold',
            color: 'var(--gold-light)',
            fontFamily: "'Orbitron', sans-serif",
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.7), 0 0 12px rgba(197, 160, 89, 0.3)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease',
          }}
          title={isIOS ? "Guía de Instalación para iPhone" : "Instalar Vortex App"}
        >
          <span style={{ fontSize: '1.1rem' }}>{isIOS ? '📱' : '📲'}</span>
          <span>{isIOS ? 'Instalar en iPhone' : 'Instalar App'}</span>
        </button>
      )}

      {showBrowserGuide && (
        <div
          onClick={() => setShowBrowserGuide(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #0d2035 0%, #071526 100%)',
              border: '1px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '16px', padding: '24px',
              maxWidth: '380px', width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.8), 0 0 20px rgba(197,160,89,0.2)',
              fontFamily: 'var(--font-primary)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <h4 style={{
                margin: 0, fontFamily: "'Orbitron', sans-serif",
                fontSize: '0.9rem', color: '#c5a059',
                letterSpacing: '1px', textTransform: 'uppercase',
              }}>Instalar Vortex App</h4>
              <button
                onClick={() => setShowBrowserGuide(false)}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'rgba(181,200,216,0.6)', fontSize: '1rem',
                  cursor: 'pointer', padding: 0, lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: '0.75rem', color: 'rgba(181,200,216,0.85)', lineHeight: 1.4 }}>
              Abre el menú de tu navegador y selecciona <strong>&quot;Añadir a pantalla de inicio&quot;</strong> o <strong>&quot;Instalar aplicación&quot;</strong>:
            </p>
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '12px',
              fontSize: '0.75rem', color: 'white',
              background: 'rgba(255,255,255,0.03)',
              padding: '12px', borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  background: 'rgba(197,160,89,0.2)', color: '#c5a059',
                  width: '20px', height: '20px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.65rem', fontWeight: 'bold', flexShrink: 0,
                }}>1</span>
                <span>Pulsa el icono de menú <strong>⁝</strong> (Chrome) o <strong>⚙</strong> (Safari) en tu navegador.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  background: 'rgba(197,160,89,0.2)', color: '#c5a059',
                  width: '20px', height: '20px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.65rem', fontWeight: 'bold', flexShrink: 0,
                }}>2</span>
                <span>Desliza y selecciona <strong>Añadir a pantalla de inicio</strong> o <strong>Instalar app</strong>.</span>
              </div>
            </div>
            <button
              onClick={() => setShowBrowserGuide(false)}
              style={{
                width: '100%', marginTop: '16px',
                background: 'rgba(197,160,89,0.1)',
                border: '1px solid rgba(197,160,89,0.3)',
                borderRadius: '6px', color: '#c5a059',
                fontFamily: "'Orbitron', sans-serif",
                fontSize: '0.7rem', fontWeight: '700',
                padding: '10px 0', cursor: 'pointer',
                textTransform: 'uppercase', letterSpacing: '1px',
              }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;